// ChainReaction — narrative beat after the 4 problem moments | 7 lines in normal
// document flow, each fading up via whileInView as it enters view and staying
// visible | muted Inter for the problem lines, serif for the payoff
//
// Deliberately NOT scroll-progress driven: a useScroll/sticky implementation
// left every line stuck at opacity 0 behind viewports of empty space. whileInView
// binds each line to its own intersection, so a line in view is always visible.

import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { colors } from './landingTokens'
import { useWindowWidth } from '../../hooks/useWindowWidth'

const EASE = [0.22, 1, 0.36, 1] as const

type LineKind = 'lead' | 'muted' | 'divider' | 'serifDim' | 'serifBright' | 'kicker'

interface NarrativeLine {
  id: string
  text?: string
  kind: LineKind
}

const NARRATIVE: NarrativeLine[] = [
  { id: 'lead', text: "Everyone's working hard. Nobody's working together.", kind: 'lead' },
  { id: 'students', text: 'Every student is juggling essays, deadlines, and lists alone.', kind: 'muted' },
  { id: 'counselors', text: 'Every counselor is tracking it all across a dozen tabs.', kind: 'muted' },
  { id: 'schools', text: 'Every school is flying blind on outcomes.', kind: 'muted' },
  { id: 'divider', kind: 'divider' },
  { id: 'effort', text: 'The problem was never effort.', kind: 'serifDim' },
  { id: 'workspace', text: 'It was the workspace.', kind: 'serifBright' },
  { id: 'until', text: 'UNTIL NOW.', kind: 'kicker' },
]

// Explicit cumulative timeline, index-aligned with NARRATIVE.
// 4 muted lines 400ms apart → divider lands with the 4th → payoff at 600ms
// intervals. Last line starts at 2.8s, settles ~3.25s.
const LINE_DELAYS = [0, 0.4, 0.8, 1.2, 1.2, 1.6, 2.2, 2.8]

function lineStyle(kind: LineKind, isMobile: boolean): React.CSSProperties {
  switch (kind) {
    case 'lead':
      return {
        fontFamily: 'Inter, sans-serif',
        fontSize: isMobile ? 20 : 27,
        fontWeight: 400,
        lineHeight: 1.7,
        color: 'rgba(255,255,255,0.9)',
        margin: 0,
      }
    case 'muted':
      return {
        fontFamily: 'Inter, sans-serif',
        fontSize: isMobile ? 17 : 22,
        fontWeight: 400,
        lineHeight: 1.7,
        color: 'rgba(255,255,255,0.55)',
        margin: 0,
      }
    case 'serifDim':
      return {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: isMobile ? 'clamp(36px, 9vw, 64px)' : 72,
        fontWeight: 700,
        color: 'rgba(255,255,255,0.7)',
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        margin: 0,
      }
    case 'serifBright':
      return {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: isMobile ? 'clamp(36px, 9vw, 64px)' : 72,
        fontWeight: 700,
        color: colors.textPrimary,
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        margin: 0,
      }
    case 'kicker':
      return {
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.4)',
        margin: 0,
      }
    default:
      return {}
  }
}

// Vertical rhythm between lines. Tuned so the whole beat occupies roughly
// 1.5–2 viewports — enough to reveal in sequence, never a black gap.
function gapAbove(kind: LineKind, index: number, isMobile: boolean): number {
  if (index === 0) return 0
  switch (kind) {
    case 'divider':
      return isMobile ? 48 : 64
    case 'serifDim':
      return isMobile ? 48 : 64
    case 'serifBright':
      return isMobile ? 8 : 12
    case 'kicker':
      return isMobile ? 40 : 56
    default:
      return isMobile ? 28 : 38
  }
}

function ChainReaction() {
  const width = useWindowWidth()
  const isMobile = width < 768
  const prefersReduced = useReducedMotion()

  return (
    <section
      id="chain-reveal"
      style={{
        background: 'transparent',
        position: 'relative',
        zIndex: 1,
        scrollMarginTop: 64,
        padding: isMobile ? '120px 24px 140px' : '180px 24px 200px',
      }}
    >
      {/* One trigger for the whole sequence. Per-line viewport triggers fired
          near-simultaneously because the lines sit close together — a parent
          stagger guarantees each lands before the next begins. */}
      <motion.div
        style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}
        initial={prefersReduced ? 'shown' : 'hidden'}
        whileInView="shown"
        viewport={{ once: true, amount: 0.35 }}
        variants={{ shown: {} }}
      >
        {NARRATIVE.map((line, i) => {
          const marginTop = gapAbove(line.kind, i, isMobile)
          // Cumulative timeline: 4 muted lines at 400ms apart, divider with
          // the 4th, then the payoff at 600ms intervals. ~3.4s end to end.
          const delay = prefersReduced ? 0 : LINE_DELAYS[i]

          if (line.kind === 'divider') {
            const base: React.CSSProperties = {
              width: 48,
              height: 1,
              background: 'rgba(255,255,255,0.15)',
              margin: `${marginTop}px auto 0`,
            }
            if (prefersReduced) return <div key={line.id} style={base} />
            return (
              <motion.div
                key={line.id}
                style={base}
                variants={{ hidden: { opacity: 0 }, shown: { opacity: 1 } }}
                transition={{ duration: 0.45, ease: EASE, delay }}
              />
            )
          }

          const style = { ...lineStyle(line.kind, isMobile), marginTop }

          if (prefersReduced) {
            return <p key={line.id} style={style}>{line.text}</p>
          }

          return (
            <motion.p
              key={line.id}
              style={style}
              variants={{
                hidden: { opacity: 0, y: 24 },
                shown: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.45, ease: EASE, delay }}
            >
              {line.text}
            </motion.p>
          )
        })}
      </motion.div>
    </section>
  )
}

export default ChainReaction
